/**
 * Learner Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { LearnerUpdate } from '../../types/learner';

const LearnerEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<LearnerUpdate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    grade_level: 0,
    ieb_exam_number: '',
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchLearner = async () => {
      if (!id) {
        setError('No learner ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/learners/${id}`);
        const learner = response.data;

        setFormData({
          first_name: learner.first_name || '',
          last_name: learner.last_name || '',
          email: learner.email || '',
          phone: learner.phone || '',
          date_of_birth: learner.date_of_birth || '',
          grade_level: learner.grade_level || 0,
          ieb_exam_number: learner.ieb_exam_number || '',
          is_active: learner.is_active ?? true,
        });
      } catch (err: any) {
        console.error('Error fetching learner:', err);
        if (err.response?.status === 404) {
          setError('Learner not found');
        } else {
          setError('Failed to load learner data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchLearner();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? 0 : Number(value)) : value)
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: LearnerUpdate = {};

      if (formData.first_name && formData.first_name.trim() !== '') payload.first_name = formData.first_name;
      if (formData.last_name && formData.last_name.trim() !== '') payload.last_name = formData.last_name;
      if (formData.email && formData.email.trim() !== '') payload.email = formData.email;
      if (formData.phone && formData.phone.trim() !== '') payload.phone = formData.phone;
      if (formData.date_of_birth && formData.date_of_birth.trim() !== '') payload.date_of_birth = formData.date_of_birth;
      if (formData.grade_level !== undefined) payload.grade_level = formData.grade_level;
      if (formData.ieb_exam_number && formData.ieb_exam_number.trim() !== '') payload.ieb_exam_number = formData.ieb_exam_number;
      if (formData.is_active !== undefined) payload.is_active = formData.is_active;

      await axios.patch(`/api/learners/${id}`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/learners');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating learner:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update learner. Please check your input.');
        }
      } else {
        setError('Failed to update learner. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData.first_name) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/learners')} sx={{ mt: 2 }}>
          Back to Learners
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/learners')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Learners
        </MuiLink>
        <Typography color="text.primary">Edit Learner</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Learner</Typography>
        <Typography variant="body2" color="textSecondary">Update the learner information</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Learner updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="First Name" name="first_name"
                value={formData.first_name || ''} onChange={handleChange}
                error={!!validationErrors.first_name} helperText={validationErrors.first_name}
                disabled={loading}
              />
              <TextField
                fullWidth label="Last Name" name="last_name"
                value={formData.last_name || ''} onChange={handleChange}
                error={!!validationErrors.last_name} helperText={validationErrors.last_name}
                disabled={loading}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={formData.email || ''} onChange={handleChange}
                error={!!validationErrors.email} helperText={validationErrors.email}
                disabled={loading}
              />
              <TextField
                fullWidth label="Phone" name="phone" type="tel"
                value={formData.phone || ''} onChange={handleChange}
                error={!!validationErrors.phone} helperText={validationErrors.phone}
                disabled={loading}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth type="date" label="Date of Birth" name="date_of_birth"
                value={formData.date_of_birth || ''} onChange={handleChange}
                error={!!validationErrors.date_of_birth} helperText={validationErrors.date_of_birth}
                disabled={loading} InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth type="number" label="Grade Level" name="grade_level"
                value={formData.grade_level || ''} onChange={handleChange}
                error={!!validationErrors.grade_level} helperText={validationErrors.grade_level || 'e.g., 10, 11, 12'}
                disabled={loading} inputProps={{ min: 1, max: 12 }}
              />
            </Stack>

            <TextField
              fullWidth label="IEB Exam Number" name="ieb_exam_number"
              value={formData.ieb_exam_number || ''} onChange={handleChange}
              error={!!validationErrors.ieb_exam_number} helperText={validationErrors.ieb_exam_number}
              disabled={loading}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active ?? true}
                  onChange={handleChange}
                  name="is_active"
                  disabled={loading}
                />
              }
              label="Active"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/learners')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Learner'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default LearnerEditForm;
